import React, { useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';

import Button from 'components/Button/Button';
import Filter from 'components/Filter/Filter';
import Tooltip from 'components/Tooltip/Tooltip';
import Pagination from 'components/Pagination/Pagination';
import DirectionIcon from 'components/DirectionIcon/DirectionIcon';

import UploadFont from '../../components/UploadUserFont/UploadFont';
import RemoveFontError from './RemoveUserFont/RemoveFont';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import colors from 'theme/Colors.module.scss';
import './Fonts.scss';

import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  TableContainer,
  Progress,
  Flex,
} from '@chakra-ui/react';

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  useGetUserFontsQuery,
  useDeleteUserFontMutation,
} from 'modules/templates/templatesApi';

export default function FontContainer() {
  const dispatch = useDispatch();
  const fileInput = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [file, setFile] = useState(null);
  const [refreshSymbol, setRefreshSymbol] = useState(Symbol());

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  // Raw search input (not debounced)
  const [searchInput, setSearchInput] = useState('');

  // Debounced search parameters
  const [defaultSearchParameters, setDefaultSearchParameters] = useState({
    search: '',
    sort: [],
  });

  const debouncedSearch = useMemo(
    () =>
      debounce(query => {
        setDefaultSearchParameters(prev => ({ ...prev, search: query }));
        setPagination(prev => ({ ...prev, pageIndex: 0 })); // Reset to first page
      }, 500), // Adjust debounce delay
    [] // No dependencies, so it's created only once
  );

  // Wrap it in useCallback so it doesn't change on every render
  const handleDebouncedSearch = useCallback(
    query => {
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  const {
    data: userFontsData,
    error,
    isLoading,
    isFetching,
  } = useGetUserFontsQuery({
    ...defaultSearchParameters,
    page: pagination.pageIndex + 1,
    size: pagination.pageSize,
    sort: defaultSearchParameters.sort || [],
    normalize: true,
  });

  const [deleteUserFont] = useDeleteUserFontMutation();

  const columns = [
    {
      accessorKey: 'fullname',
      header: 'Font Name',
      enableSorting: false,
      cell: props => <Text>{props.getValue()}</Text>,
    },
    {
      accessorKey: 'family',
      header: 'Font Family',
      enableSorting: false,
      cell: props => <Text>{props.getValue()}</Text>,
    },
    {
      accessorKey: '',
      header: 'Action',
      enableSorting: false,
      cell: props => {
        const { id } = props.row.original;
        return (
          <Tooltip
            tipText={'Delete Font.'}
            direction="left"
            render={() => (
              <Button
                icon="delete"
                iconColor={colors.slate}
                btnStyle="transparent"
                onClick={() => {
                  handleDeleteUserFont(id);
                }}
              />
            )}
          />
        );
      },
    },
  ];

  const table = useReactTable({
    data: userFontsData?.data.entities || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true, // Enable manual pagination
    state: {
      pagination,
    },
  });

  if (error) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch font data',
      })
    );
  }

  // Handle sort clicked
  const handleSortClick = header => {
    if (header.column.getNextSortingOrder() === 'asc') {
      header.column.toggleSorting(false); // Set to 'asc'
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [
          {
            field: header.id,
            direction: 'asc',
          },
        ],
      });
    } else if (header.column.getNextSortingOrder() === 'desc') {
      header.column.toggleSorting(true); // Set to 'desc'
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [
          {
            field: header.id,
            direction: 'desc',
          },
        ],
      });
    } else {
      header.column.clearSorting(); // Remove sorting
      setDefaultSearchParameters({
        ...defaultSearchParameters,
        sort: [],
      });
    }
  };

  // Handle rows per clicked
  const handleRowClick = row => {
    setSelectedRowId(prev => (prev === row.id ? null : row.id)); // Toggle selection
  };

  const handleDeleteUserFont = async id => {
    try {
      await deleteUserFont(id).unwrap();
      dispatch(
        pushNotificationWithTimeout({
          type: 'success',
          message: 'Font removed successfully',
        })
      );
    } catch (error) {
      dispatch(
        pushNotificationWithTimeout({
          type: 'error',
          message:
            'There was an error trying to remove the font, please try again',
        })
      );
    }
  };

  const closeUploadFontModal = () => {
    setShowModal(false);
    setFile(null);
    if (fileInput?.current) {
      fileInput.current.value = null;
    }
  };

  const handleFontFamilyChange = res => {
    setRefreshSymbol(Symbol());
  };

  const onSuccess = () =>
    dispatch(
      pushNotificationWithTimeout({
        type: 'success',
        message: 'Font upload successfully',
      })
    );

  return (
    <>
      <Box className="fonts-header">
        <Box className="fonts-header__left">
          <Button
            type="button"
            icon="add"
            text="New Font"
            textColor={colors.slate}
            btnStyle="transparent"
            onClick={() => {
              fileInput.current.value = '';
              fileInput?.current?.click();
            }}
          />
          <input
            hidden
            type="file"
            ref={fileInput}
            accept=".ttf, .otf"
            multiple={false}
            onChange={ev => {
              setFile(ev.target.files[0]);
              if (ev.target.files[0]) {
                setShowModal(true);
              }
            }}
          />
        </Box>
        <Box className="fonts-header__nav"></Box>
        <Box className="fonts-header__right"></Box>
      </Box>

      <Box className="fonts-body">
        <Box height="60px" className="rail rail-rule">
          <Filter
            placeholder="Search fonts"
            disabled={true} // temporary disabled
            value={searchInput}
            onChange={event => {
              setSearchInput(event); // Update raw input immediately
              handleDebouncedSearch(event); // Trigger debounced update
            }}
          />
        </Box>

        <Box className="fonts-table" borderWidth="1px">
          <TableContainer>
            {(isLoading || isFetching) && (
              <Progress size="xs" isIndeterminate />
            )}
            <Table size="md" variant="simple">
              <Thead zIndex={1} bg="white">
                {table.getHeaderGroups().map(headerGroup => (
                  <Tr className="fonts-table__row" key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                      const textAlign =
                        header.column.columnDef.textAlign || 'left';
                      return (
                        <Th
                          className="fonts-table__header"
                          key={header.id}
                          whiteSpace="nowrap"
                          textAlign={textAlign}
                        >
                          {header.column.getCanSort() ? (
                            <Flex justify={textAlign} align="center">
                              <Text
                                className="fonts-table__header-text"
                                mr={2}
                                onClick={() => handleSortClick(header)}
                                cursor={
                                  header.column.getCanSort()
                                    ? 'pointer'
                                    : 'default'
                                }
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                              </Text>

                              <Box
                                onClick={() => handleSortClick(header)}
                                cursor={
                                  header.column.getCanSort()
                                    ? 'pointer'
                                    : 'default'
                                }
                              >
                                {defaultSearchParameters.sort[0]?.field ===
                                header.id ? (
                                  <DirectionIcon
                                    type={'alpha'}
                                    direction={
                                      defaultSearchParameters.sort[0]
                                        ?.direction || 'asc'
                                    }
                                    style={{
                                      color:
                                        defaultSearchParameters.sort[0]
                                          ?.field === header.id
                                          ? colors.primary900
                                          : colors.neutral300,
                                    }}
                                  />
                                ) : (
                                  <DirectionIcon
                                    type={'alpha'}
                                    direction={'normal'}
                                    style={{
                                      color:
                                        defaultSearchParameters.sort[0]
                                          ?.field === header.id
                                          ? colors.primary900
                                          : colors.neutral300,
                                    }}
                                  />
                                )}
                              </Box>
                            </Flex>
                          ) : (
                            <Text className="fonts-table__header-text" mr={2}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </Text>
                          )}
                        </Th>
                      );
                    })}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map(row => {
                  return (
                    <Tr
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                      bg={row.id === selectedRowId ? 'gray.100' : 'transparent'}
                      color={colors.slate}
                      _hover={{
                        bg: colors.lighterGrey,
                        textColor: colors.deepSlate2,
                      }}
                      cursor="pointer"
                    >
                      {row.getVisibleCells().map((cell, index) => {
                        const textAlign =
                          cell.column.columnDef.textAlign || 'left';

                        return (
                          <Td
                            className="fonts-table__body-cell"
                            px={index === 0 ? '0px' : '15px'}
                            py="0px"
                            key={cell.id}
                            textAlign={textAlign}
                          >
                            {index === 0 ? (
                              <Box className="fonts-table__first-cell-container">
                                <Box
                                  className="fonts-table__indicator"
                                  bg={`${
                                    row.original.inactive_at
                                      ? 'gray.400'
                                      : 'green.400'
                                  }`}
                                ></Box>
                                <Box className="fonts-table__first-cell">
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </Box>
                              </Box>
                            ) : (
                              flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
        <Pagination
          table={table}
          data={userFontsData}
          pagination={pagination}
          setPagination={setPagination}
          setSelectedRowId={setSelectedRowId}
        />
      </Box>
      <UploadFont
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        closeUploadFontModal={closeUploadFontModal}
        handleFontFamilyChange={handleFontFamilyChange}
        file={file}
        onSuccess={onSuccess}
      />
      <RemoveFontError
        isOpen={showRemoveModal}
        onClose={() => setShowRemoveModal(false)}
      />
    </>
  );
}
